import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
    Button,
    Form,
    Input,
    Switch,
    Spin,
    Card,
    Row,
    Col,
    message,
    Space,
    Table,
    Tag,
    DatePicker,
    Select,
    Modal,
    Typography,
    Tooltip,
    Badge,
    Statistic
} from 'antd';
import {
    SaveOutlined,
    ReloadOutlined,
    ApiOutlined,
    SendOutlined,
    HistoryOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SettingOutlined,
    MessageOutlined,
    PhoneOutlined,
    UserOutlined,
    CarOutlined,
    InfoCircleOutlined,
    ClearOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from "react-i18next";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

// Estilos CSS para la animación de pulso del indicador de servicio activo
const pulseKeyframes = `
@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.7);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(82, 196, 26, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(82, 196, 26, 0);
    }
}
@keyframes pulseInactive {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.5;
    }
    100% {
        opacity: 1;
    }
}
/* Estilos para el switch de servicio WhatsApp */
.whatsapp-service-switch.ant-switch-checked {
    background-color: #25D366 !important;
}
.whatsapp-service-switch:not(.ant-switch-checked) {
    background-color: #8c8c8c !important;
}
`;

// Inyectar estilos de animación
if (typeof document !== 'undefined' && !document.getElementById('whatsapp-pulse-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'whatsapp-pulse-styles';
    styleSheet.textContent = pulseKeyframes;
    document.head.appendChild(styleSheet);
}

export const Axios = axios.create({
    baseURL: process.env.REACT_APP_APPI_ADDRESS
});

const WhatsAppConfig = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [testForm] = Form.useForm();
    const [spinning, setSpinning] = useState(false);
    const [spinTitle, setSpinTitle] = useState('Cargando...');
    const [config, setConfig] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [testModalVisible, setTestModalVisible] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();
    const [activeTab, setActiveTab] = useState('config');
    const [logFilters, setLogFilters] = useState({});

    const headers = {
        'accept': '*/*',
        'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
        'Content-Type': 'application/json',
    };

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = () => {
        setSpinning(true);
        setSpinTitle('Cargando configuración...');

        Axios.get('sd/whatsapp/config', { headers })
            .then(response => {
                setSpinning(false);
                if (response.data.success && response.data.data) {
                    const data = response.data.data;
                    setConfig(data);
                    form.setFieldsValue({
                        idconfwhatsapp: data.idconfwhatsapp,
                        habilitado: data.habilitado,
                        apikey: '',
                        notificar_despacho: data.notificar_despacho,
                        notificar_ruta_iniciada: data.notificar_ruta_iniciada,
                        notificar_remitente: data.notificar_remitente,
                        notificar_destinatario: data.notificar_destinatario,
                        plantilla_despacho: data.plantilla_despacho,
                        plantilla_ruta_iniciada: data.plantilla_ruta_iniciada,
                        codigo_pais: data.codigo_pais || '+1',
                    });
                }
            })
            .catch(error => {
                setSpinning(false);
                messageApi.error('Error al cargar la configuración');
                console.error(error);
            });
    };

    const saveConfig = (values) => {
        setSpinning(true);
        setSpinTitle('Guardando configuración...');

        const payload = {
            idconfwhatsapp: config?.idconfwhatsapp || 1,
            habilitado: values.habilitado || false,
            apikey: values.apikey || null,
            notificar_despacho: values.notificar_despacho || false,
            notificar_ruta_iniciada: values.notificar_ruta_iniciada || false,
            notificar_remitente: values.notificar_remitente || false,
            notificar_destinatario: values.notificar_destinatario || false,
            plantilla_despacho: values.plantilla_despacho || '',
            plantilla_ruta_iniciada: values.plantilla_ruta_iniciada || '',
            codigo_pais: values.codigo_pais || '+1',
        };

        Axios.post('sd/whatsapp/config', payload, { headers })
            .then(response => {
                setSpinning(false);
                if (response.data.success) {
                    messageApi.success('Configuración guardada correctamente');
                    loadConfig();
                } else {
                    messageApi.error(response.data.message || 'Error al guardar');
                }
            })
            .catch(error => {
                setSpinning(false);
                messageApi.error('Error al guardar la configuración');
                console.error(error);
            });
    };

    const testConnection = () => {
        setSpinning(true);
        setSpinTitle('Probando conexión...');
        setConnectionStatus(null);

        Axios.post('sd/whatsapp/test-connection', {}, { headers })
            .then(response => {
                setSpinning(false);
                if (response.data.success) {
                    setConnectionStatus('success');
                    messageApi.success('Conexión exitosa con WASenderAPI');
                } else {
                    setConnectionStatus('error');
                    messageApi.error(response.data.message || 'Error de conexión');
                }
            })
            .catch(error => {
                setSpinning(false);
                setConnectionStatus('error');
                messageApi.error('Error al probar la conexión');
                console.error(error);
            });
    };

    const sendTestMessage = (values) => {
        setSpinning(true);
        setSpinTitle('Enviando mensaje de prueba...');

        Axios.post('sd/whatsapp/send-test', {
            telefono: values.telefono,
            mensaje: values.mensaje
        }, { headers })
            .then(response => {
                setSpinning(false);
                if (response.data.success) {
                    messageApi.success('Mensaje enviado correctamente');
                    setTestModalVisible(false);
                    testForm.resetFields();
                    if (activeTab === 'logs') {
                        loadLogs(logFilters);
                    }
                } else {
                    messageApi.error(response.data.message || 'Error al enviar mensaje');
                }
            })
            .catch(error => {
                setSpinning(false);
                messageApi.error('Error al enviar el mensaje de prueba');
                console.error(error);
            });
    };

    const loadLogs = (filters = {}) => {
        setLogsLoading(true);
        setLogFilters(filters);

        Axios.post('sd/whatsapp/logs', filters, { headers })
            .then(response => {
                setLogsLoading(false);
                if (response.data.success) {
                    setLogs(response.data.data || []);
                }
            })
            .catch(error => {
                setLogsLoading(false);
                messageApi.error('Error al cargar el historial');
                console.error(error);
            });
    };

    const clearLogFilters = () => {
        setLogFilters({});
        loadLogs({});
    };

    // Estadísticas de logs
    const logStats = useMemo(() => {
        if (!logs.length) {
            return { total: 0, enviados: 0, fallidos: 0 };
        }
        return {
            total: logs.length,
            enviados: logs.filter(l => l.estado === 'enviado').length,
            fallidos: logs.filter(l => l.estado === 'fallido').length
        };
    }, [logs]);

    const logColumns = [
        {
            title: 'Fecha',
            dataIndex: 'fecha_envio',
            key: 'fecha_envio',
            width: 160,
            render: (text) => (
                <span style={{ color: '#495057', fontSize: '13px' }}>
                    {new Date(text).toLocaleString()}
                </span>
            ),
            sorter: (a, b) => new Date(a.fecha_envio) - new Date(b.fecha_envio),
        },
        {
            title: 'Teléfono',
            dataIndex: 'telefono',
            key: 'telefono',
            width: 140,
            render: (text) => (
                <span style={{ fontWeight: '500', color: '#0F1960' }}>
                    <PhoneOutlined style={{ marginRight: 4 }} />
                    {text}
                </span>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo_evento',
            key: 'tipo_evento',
            width: 130,
            render: (text) => {
                const config = {
                    'despacho': { color: '#1890ff', icon: <CarOutlined />, label: 'Despacho' },
                    'ruta_iniciada': { color: '#52c41a', icon: <SendOutlined />, label: 'Ruta Iniciada' },
                    'prueba': { color: '#faad14', icon: <MessageOutlined />, label: 'Prueba' }
                };
                const cfg = config[text] || { color: '#666', icon: <MessageOutlined />, label: text };
                return (
                    <Tag color={cfg.color} style={{ borderRadius: '4px' }}>
                        {cfg.icon} {cfg.label}
                    </Tag>
                );
            },
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            key: 'estado',
            width: 110,
            render: (text) => (
                <Tag
                    color={text === 'enviado' ? 'success' : 'error'}
                    style={{ borderRadius: '4px', fontWeight: '500' }}
                >
                    {text === 'enviado' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    {' '}{text?.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Mensaje',
            dataIndex: 'mensaje',
            key: 'mensaje',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span style={{ color: '#495057', fontSize: '13px' }}>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Error',
            dataIndex: 'error',
            key: 'error',
            width: 150,
            ellipsis: true,
            render: (text) => text ? (
                <Tooltip title={text}>
                    <span style={{ color: '#ff4d4f', fontSize: '12px' }}>{text}</span>
                </Tooltip>
            ) : (
                <span style={{ color: '#bfbfbf' }}>-</span>
            ),
        },
    ];

    const variablesDisponibles = [
        { var: '{codigo}', desc: 'Código de factura', icon: <InfoCircleOutlined /> },
        { var: '{destinatario}', desc: 'Nombre destinatario', icon: <UserOutlined /> },
        { var: '{remitente}', desc: 'Nombre remitente', icon: <UserOutlined /> },
        { var: '{direccion}', desc: 'Dirección de entrega', icon: <SettingOutlined /> },
        { var: '{provincia}', desc: 'Provincia de entrega', icon: <EnvironmentOutlined /> },
        { var: '{municipio}', desc: 'Municipio de entrega', icon: <EnvironmentOutlined /> },
        { var: '{chofer}', desc: 'Nombre chofer (si asignado)', icon: <CarOutlined /> },
        { var: '{vehiculo}', desc: 'Matrícula (si asignado)', icon: <CarOutlined /> },
        { var: '{fecha}', desc: 'Fecha actual', icon: <HistoryOutlined /> },
    ];

    const renderConfigTab = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={saveConfig}
            initialValues={{
                habilitado: false,
                notificar_despacho: true,
                notificar_ruta_iniciada: true,
                notificar_remitente: true,
                notificar_destinatario: true,
                codigo_pais: '+1',
            }}
        >
            <Form.Item name="idconfwhatsapp" hidden>
                <Input />
            </Form.Item>

            <Row gutter={[16, 16]}>
                {/* Estado del Servicio */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ color: '#0F1960', fontWeight: '600' }}>
                                <SettingOutlined style={{ marginRight: 8 }} />
                                Estado del Servicio
                            </span>
                        }
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%'
                        }}
                        headStyle={{
                            borderBottom: '2px solid #25D366',
                            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                        }}
                    >
                        <Row gutter={[16, 16]} align="middle">
                            <Col span={12}>
                                <Form.Item
                                    name="habilitado"
                                    label={<span style={{ fontWeight: '500' }}>Servicio WhatsApp</span>}
                                    valuePropName="checked"
                                    style={{ marginBottom: 0 }}
                                >
                                    <Switch
                                        checkedChildren="Activo"
                                        unCheckedChildren="Inactivo"
                                        className="whatsapp-service-switch"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <div style={{ textAlign: 'center' }}>
                                    <Button
                                        icon={<ApiOutlined />}
                                        onClick={testConnection}
                                        type={connectionStatus === 'success' ? 'primary' : 'default'}
                                        style={{
                                            borderRadius: '6px',
                                            background: connectionStatus === 'success' ? '#25D366' : undefined,
                                            borderColor: connectionStatus === 'success' ? '#25D366' : undefined
                                        }}
                                    >
                                        Probar Conexión
                                    </Button>
                                    {connectionStatus && (
                                        <div style={{ marginTop: 8 }}>
                                            <Tag color={connectionStatus === 'success' ? 'success' : 'error'}>
                                                {connectionStatus === 'success' ? (
                                                    <><CheckCircleOutlined /> Conectado</>
                                                ) : (
                                                    <><CloseCircleOutlined /> Error</>
                                                )}
                                            </Tag>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Credenciales API */}
                <Col xs={24} lg={12}>
                    <Card
                        title={
                            <span style={{ color: '#0F1960', fontWeight: '600' }}>
                                <ApiOutlined style={{ marginRight: 8 }} />
                                Credenciales API
                            </span>
                        }
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%'
                        }}
                        headStyle={{
                            borderBottom: '2px solid #1890ff',
                            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                        }}
                    >
                        <Form.Item
                            name="apikey"
                            label={<span style={{ fontWeight: '500' }}>API Key de WASenderAPI</span>}
                            extra={
                                config?.has_apikey ? (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                                        Configurada: {config.apikey_masked}
                                    </Text>
                                ) : (
                                    <Text type="warning" style={{ fontSize: '12px' }}>
                                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                                        No hay API Key configurada
                                    </Text>
                                )
                            }
                        >
                            <Input.Password
                                placeholder="Ingrese API Key (dejar vacío para mantener actual)"
                                style={{ borderRadius: '6px' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="codigo_pais"
                            label={<span style={{ fontWeight: '500' }}>Código de País por Defecto</span>}
                            rules={[{ required: true, message: 'Requerido' }]}
                        >
                            <Select style={{ borderRadius: '6px' }}>
                                <Option value="+1">+1 (USA/Canada)</Option>
                                <Option value="+52">+52 (México)</Option>
                                <Option value="+53">+53 (Cuba)</Option>
                                <Option value="+34">+34 (España)</Option>
                                <Option value="+57">+57 (Colombia)</Option>
                                <Option value="+58">+58 (Venezuela)</Option>
                            </Select>
                        </Form.Item>
                    </Card>
                </Col>

                {/* Opciones de Notificación */}
                <Col xs={24}>
                    <Card
                        title={
                            <span style={{ color: '#0F1960', fontWeight: '600' }}>
                                <MessageOutlined style={{ marginRight: 8 }} />
                                Opciones de Notificación
                            </span>
                        }
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                        headStyle={{
                            borderBottom: '2px solid #722ed1',
                            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                        }}
                    >
                        <Row gutter={[24, 16]}>
                            <Col xs={24} md={12}>
                                <Card
                                    size="small"
                                    style={{
                                        background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                                        borderRadius: '8px',
                                        border: '1px solid #b7eb8f'
                                    }}
                                >
                                    <Text strong style={{ display: 'block', marginBottom: 12, color: '#389e0d' }}>
                                        <SendOutlined /> Eventos a Notificar
                                    </Text>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Form.Item
                                                name="notificar_despacho"
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                                noStyle
                                            >
                                                <Switch size="small" />
                                            </Form.Item>
                                            <Text>Al Despachar Paquete</Text>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Form.Item
                                                name="notificar_ruta_iniciada"
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                                noStyle
                                            >
                                                <Switch size="small" />
                                            </Form.Item>
                                            <Text>Al Iniciar Ruta</Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>
                            <Col xs={24} md={12}>
                                <Card
                                    size="small"
                                    style={{
                                        background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                                        borderRadius: '8px',
                                        border: '1px solid #91d5ff'
                                    }}
                                >
                                    <Text strong style={{ display: 'block', marginBottom: 12, color: '#0050b3' }}>
                                        <UserOutlined /> Destinatarios
                                    </Text>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Form.Item
                                                name="notificar_destinatario"
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                                noStyle
                                            >
                                                <Switch size="small" />
                                            </Form.Item>
                                            <Text>Notificar al Destinatario</Text>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Form.Item
                                                name="notificar_remitente"
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                                noStyle
                                            >
                                                <Switch size="small" />
                                            </Form.Item>
                                            <Text>Notificar al Remitente</Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Plantillas de Mensajes */}
                <Col xs={24}>
                    <Card
                        title={
                            <span style={{ color: '#0F1960', fontWeight: '600' }}>
                                <MessageOutlined style={{ marginRight: 8 }} />
                                Plantillas de Mensajes
                            </span>
                        }
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                        headStyle={{
                            borderBottom: '2px solid #fa8c16',
                            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
                        }}
                    >
                        <Card
                            size="small"
                            style={{
                                marginBottom: 16,
                                background: '#fffbe6',
                                borderRadius: '8px',
                                border: '1px solid #ffe58f'
                            }}
                        >
                            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                <InfoCircleOutlined style={{ color: '#faad14', marginRight: 4 }} />
                                Variables Disponibles
                            </Text>
                            <Space wrap size={[8, 8]}>
                                {variablesDisponibles.map(v => (
                                    <Tooltip key={v.var} title={v.desc}>
                                        <Tag
                                            color="blue"
                                            style={{
                                                borderRadius: '4px',
                                                cursor: 'help',
                                                padding: '2px 8px'
                                            }}
                                        >
                                            {v.icon} {v.var}
                                        </Tag>
                                    </Tooltip>
                                ))}
                            </Space>
                        </Card>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} lg={12}>
                                <Form.Item
                                    name="plantilla_despacho"
                                    label={
                                        <span style={{ fontWeight: '500' }}>
                                            <CarOutlined style={{ marginRight: 4 }} />
                                            Mensaje de Despacho
                                        </span>
                                    }
                                >
                                    <TextArea
                                        rows={4}
                                        placeholder="Ej: Hola {destinatario}, su paquete {codigo} ha sido despachado..."
                                        style={{ borderRadius: '6px' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} lg={12}>
                                <Form.Item
                                    name="plantilla_ruta_iniciada"
                                    label={
                                        <span style={{ fontWeight: '500' }}>
                                            <SendOutlined style={{ marginRight: 4 }} />
                                            Mensaje de Inicio de Ruta
                                        </span>
                                    }
                                >
                                    <TextArea
                                        rows={4}
                                        placeholder="Ej: Hola {destinatario}, su paquete {codigo} está EN CAMINO..."
                                        style={{ borderRadius: '6px' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Botones de Acción */}
            <Card
                style={{
                    marginTop: 16,
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                size="large"
                                style={{
                                    borderRadius: '6px',
                                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)'
                                }}
                            >
                                Guardar Configuración
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadConfig}
                                size="large"
                                style={{ borderRadius: '6px' }}
                            >
                                Recargar
                            </Button>
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            icon={<SendOutlined />}
                            onClick={() => setTestModalVisible(true)}
                            size="large"
                            style={{
                                borderRadius: '6px',
                                borderColor: '#25D366',
                                color: '#25D366'
                            }}
                        >
                            Enviar Mensaje de Prueba
                        </Button>
                    </Col>
                </Row>
            </Card>
        </Form>
    );

    const renderLogsTab = () => (
        <div>
            {/* Stats Cards */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8}>
                    <Card
                        style={{
                            background: 'linear-gradient(135deg, #0F1960 0%, #3949ab 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(15, 25, 96, 0.2)'
                        }}
                        styles={{ body: { padding: '16px' } }}
                    >
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>TOTAL MENSAJES</span>}
                            value={logStats.total}
                            valueStyle={{ color: 'white', fontWeight: '700' }}
                            prefix={<MessageOutlined style={{ color: '#7986cb' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card
                        style={{
                            background: 'linear-gradient(135deg, #135200 0%, #389e0d 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(19, 82, 0, 0.2)'
                        }}
                        styles={{ body: { padding: '16px' } }}
                    >
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>ENVIADOS</span>}
                            value={logStats.enviados}
                            valueStyle={{ color: 'white', fontWeight: '700' }}
                            prefix={<CheckCircleOutlined style={{ color: '#95de64' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card
                        style={{
                            background: 'linear-gradient(135deg, #a8071a 0%, #cf1322 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(168, 7, 26, 0.2)'
                        }}
                        styles={{ body: { padding: '16px' } }}
                    >
                        <Statistic
                            title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>FALLIDOS</span>}
                            value={logStats.fallidos}
                            valueStyle={{ color: 'white', fontWeight: '700' }}
                            prefix={<CloseCircleOutlined style={{ color: '#ffa39e' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filtros */}
            <Card
                style={{
                    marginBottom: 16,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
            >
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} md={6}>
                        <RangePicker
                            style={{ width: '100%', borderRadius: '6px' }}
                            placeholder={['Fecha inicio', 'Fecha fin']}
                            onChange={(dates) => {
                                if (dates) {
                                    loadLogs({
                                        ...logFilters,
                                        fecha_desde: dates[0].format('YYYY-MM-DD'),
                                        fecha_hasta: dates[1].format('YYYY-MM-DD'),
                                    });
                                }
                            }}
                        />
                    </Col>
                    <Col xs={12} sm={4} md={3}>
                        <Select
                            placeholder="Estado"
                            allowClear
                            style={{ width: '100%', borderRadius: '6px' }}
                            onChange={(value) => loadLogs({ ...logFilters, estado: value })}
                        >
                            <Option value="enviado">Enviado</Option>
                            <Option value="fallido">Fallido</Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={4} md={3}>
                        <Select
                            placeholder="Tipo"
                            allowClear
                            style={{ width: '100%', borderRadius: '6px' }}
                            onChange={(value) => loadLogs({ ...logFilters, tipo_evento: value })}
                        >
                            <Option value="despacho">Despacho</Option>
                            <Option value="ruta_iniciada">Ruta Iniciada</Option>
                            <Option value="prueba">Prueba</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => loadLogs(logFilters)}
                                style={{ borderRadius: '6px' }}
                            >
                                Actualizar
                            </Button>
                            <Button
                                icon={<ClearOutlined />}
                                onClick={clearLogFilters}
                                style={{ borderRadius: '6px' }}
                            >
                                Limpiar
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Tabla */}
            <Card
                style={{
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
            >
                <Table
                    size="small"
                    bordered
                    columns={logColumns}
                    dataSource={logs.map((log, idx) => ({ ...log, key: log.idlogwhatsapp || idx }))}
                    loading={logsLoading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `${total} registros`
                    }}
                    scroll={{ x: 900 }}
                />
            </Card>
        </div>
    );

    return (
        <div style={{
            minHeight: "100vh",
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: "16px"
        }}>
            <Spin spinning={spinning} tip={spinTitle} size="large">
                <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                    {contextHolder}

                    {/* Header */}
                    <Card style={{
                        borderRadius: '8px',
                        marginBottom: '16px',
                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)'
                    }}>
                        <Row align="middle" justify="space-between">
                            <Col>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <FaWhatsapp style={{ color: 'white', fontSize: '28px' }} />
                                    </div>
                                    <div>
                                        <Title level={4} style={{ color: 'white', margin: 0 }}>
                                            Configuración de Notificaciones WhatsApp
                                        </Title>
                                        <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>
                                            Gestiona el envío automático de mensajes a clientes
                                        </Text>
                                    </div>
                                </div>
                            </Col>
                            <Col>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    background: config?.habilitado
                                        ? 'rgba(82, 196, 26, 0.25)'
                                        : 'rgba(0, 0, 0, 0.2)',
                                    padding: '8px 16px',
                                    borderRadius: '20px',
                                    border: config?.habilitado
                                        ? '2px solid rgba(82, 196, 26, 0.5)'
                                        : '2px solid rgba(255, 255, 255, 0.3)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    <div style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        background: config?.habilitado ? '#52c41a' : '#8c8c8c',
                                        animation: config?.habilitado
                                            ? 'pulse 1.5s infinite'
                                            : 'pulseInactive 2s infinite',
                                    }} />
                                    <span style={{
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {config?.habilitado ? 'Servicio Activo' : 'Servicio Inactivo'}
                                    </span>
                                    {config?.habilitado && (
                                        <CheckCircleOutlined style={{
                                            color: '#52c41a',
                                            fontSize: '16px'
                                        }} />
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    {/* Tab Navigation */}
                    <Card
                        style={{
                            marginBottom: '16px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}
                        styles={{ body: { padding: '8px 16px' } }}
                    >
                        <Space size="middle">
                            <Button
                                type={activeTab === 'config' ? 'primary' : 'text'}
                                icon={<SettingOutlined />}
                                onClick={() => setActiveTab('config')}
                                style={{
                                    borderRadius: '6px',
                                    ...(activeTab === 'config' && {
                                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                        border: 'none'
                                    })
                                }}
                            >
                                Configuración
                            </Button>
                            <Button
                                type={activeTab === 'logs' ? 'primary' : 'text'}
                                icon={<HistoryOutlined />}
                                onClick={() => {
                                    setActiveTab('logs');
                                    if (logs.length === 0) loadLogs();
                                }}
                                style={{
                                    borderRadius: '6px',
                                    ...(activeTab === 'logs' && {
                                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                        border: 'none'
                                    })
                                }}
                            >
                                Historial de Mensajes
                            </Button>
                        </Space>
                    </Card>

                    {/* Content */}
                    {activeTab === 'config' ? renderConfigTab() : renderLogsTab()}

                    {/* Modal de Prueba */}
                    <Modal
                        title={
                            <span>
                                <FaWhatsapp style={{ color: '#25D366', marginRight: 8 }} />
                                Enviar Mensaje de Prueba
                            </span>
                        }
                        open={testModalVisible}
                        onCancel={() => {
                            setTestModalVisible(false);
                            testForm.resetFields();
                        }}
                        footer={null}
                        style={{ borderRadius: '8px' }}
                    >
                        <Form
                            form={testForm}
                            layout="vertical"
                            onFinish={sendTestMessage}
                        >
                            <Form.Item
                                name="telefono"
                                label="Número de Teléfono"
                                rules={[
                                    { required: true, message: 'Ingrese el número' },
                                    { min: 7, message: 'Número muy corto' }
                                ]}
                            >
                                <Input
                                    placeholder="+1234567890"
                                    prefix={<PhoneOutlined style={{ color: '#25D366' }} />}
                                    style={{ borderRadius: '6px' }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="mensaje"
                                label="Mensaje"
                                rules={[
                                    { required: true, message: 'Ingrese el mensaje' },
                                    { max: 500, message: 'Máximo 500 caracteres' }
                                ]}
                                initialValue="Este es un mensaje de prueba desde Smart Delivery"
                            >
                                <TextArea
                                    rows={3}
                                    showCount
                                    maxLength={500}
                                    style={{ borderRadius: '6px' }}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                                <Space>
                                    <Button
                                        onClick={() => {
                                            setTestModalVisible(false);
                                            testForm.resetFields();
                                        }}
                                        style={{ borderRadius: '6px' }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SendOutlined />}
                                        style={{
                                            borderRadius: '6px',
                                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                            border: 'none'
                                        }}
                                    >
                                        Enviar
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </Spin>
        </div>
    );
};

export default WhatsAppConfig;
